import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { availableSearchSources, SearchType } from 'fym-common/src/types/searchService';
import { TypedMemo } from '../../../common/types';
import { useLazySearchQuery } from '../../../../redux/api/musicAPI';
import { ReleaseLinkData } from './types';
import { parseSearchResults } from './utils';
import { useReleaseLinkForm } from '../forms/ReleaseLinkForm/useReleaseLinkForm';
import { ReleaseLinkWizardFormViewProps } from './props';
import { SMLAnalytics, smartLinkMediaOf } from '../../../analytics/sml/analytics';
import { ReleaseLinkWizardFormViewRenderer } from './ReleaseLinkWizardFormViewRenderer';
import { Alert } from '../../../forms/components/Alert/Alert';
import { AlertLevel } from '../../../forms/components/Alert/props';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { CatalogTrackSearchStep, isCatalogTrackSearchSupported } from './CatalogTrackSearchStep';

const ReleaseLinkWizardFormViewComponent: React.FC<ReleaseLinkWizardFormViewProps> = ({
  accountId,
  initialCatalogTrackMaid,
  onResults,
}) => {
  const intl = useIntl();
  const [isCatalogMode, setIsCatalogMode] = useState(isCatalogTrackSearchSupported);
  const [startFromMaid, setStartFromMaid] = useState(initialCatalogTrackMaid);
  const [search, { error: searchError, isLoading: isSearchLoading }] = useLazySearchQuery();

  const useUrlMode = useCallback(() => {
    // The starting release goes with the step. Coming back to catalog mode
    // after leaving it means the artist wants to pick something else, and
    // resolving the same track again would be a loop.
    setStartFromMaid(undefined);
    setIsCatalogMode(false);
  }, []);

  const useCatalogMode = useCallback(() => {
    setIsCatalogMode(true);
  }, []);

  const onSuccess = useCallback(
    (releaseLinkData: ReleaseLinkData) => {
      const { data, type, source } = releaseLinkData;
      if (type === SearchType.unknown) {
        onResults(releaseLinkData, []);
        return;
      }
      search({
        track: data.track ?? undefined,
        album: data.album ?? undefined,
        artist: data.artist ?? undefined,
        sources: availableSearchSources.filter((importer) => importer !== source),
        type,
      })
        .then((searchResult) => {
          if (!searchResult.isSuccess) {
            return;
          }
          const { tracks, albums } = searchResult.data;
          const items = parseSearchResults([...(tracks ?? []), ...(albums ?? [])]);

          onResults(releaseLinkData, items);
          SMLAnalytics.trackSmartLinkCreateStarted('release', smartLinkMediaOf(type)).catch(console.error);
        })
        .catch(console.error);
    },
    [onResults, search]
  );

  const {
    isLoading: isReleaseLinkLoading,
    error: releaseLinkError,
    submit,
    MediaUrlInput,
  } = useReleaseLinkForm({ onSuccess, useMinimalistic: false });

  return (
    <ReleaseLinkWizardFormViewRenderer
      isLoading={isReleaseLinkLoading || isSearchLoading}
      error={releaseLinkError ?? searchError}
      onSubmit={isCatalogMode ? undefined : submit}
    >
      {isCatalogMode ? (
        <CatalogTrackSearchStep
          accountId={accountId}
          initialTrackMaid={startFromMaid}
          onResults={onResults}
          onUseUrlInstead={useUrlMode}
        />
      ) : (
        <>
          {MediaUrlInput}
          <Alert level={AlertLevel.info} center={false}>
            {intl.formatMessage({
              id: 'app.forms.track_link.help2',
              defaultMessage:
                'Using this link we will create a music service widget and search for a match on all other music streaming services.',
            })}
          </Alert>
          {isCatalogTrackSearchSupported && (
            <SMLButton color="link" onClick={useCatalogMode}>
              {intl.formatMessage({
                id: 'app.smart_links.release_smart_link.catalog.use_search',
                defaultMessage: 'Search our catalog instead',
              })}
            </SMLButton>
          )}
        </>
      )}
    </ReleaseLinkWizardFormViewRenderer>
  );
};

export const ReleaseLinkWizardFormView = TypedMemo(ReleaseLinkWizardFormViewComponent);
